// ─── Date range for the Tasks worklist ───────────────────────────────────────
//
// The Tasks tab had no date control: it listed every maintenance event a tenant
// had, and the reservation layer used a hardcoded window. This gives it a real
// range, shared by both feeds so they can never disagree about what "this
// month" means.
//
// LOAD-BEARING: both backend endpoints are inclusive-start / EXCLUSIVE-end
// (`dueDate.gte(from)` + `.lt(to)` for events; `startsAt < to` for
// reservations). The UI shows an inclusive range because "Aug 1 → Aug 31" is
// what a human means, so `toQueryRange` is the one place that converts. Skip it
// and the last selected day silently disappears from the list.
//
// Pure and dependency-free so it can be unit-tested.

export type RangePreset = 'today' | 'week' | 'month' | 'all'

/** What the user sees: both ends inclusive. `null` = no date filter. */
export type DisplayRange = { from: string; to: string } | null

/** What the API gets: `to` is exclusive. Both undefined = no date filter. */
export type QueryRange = { from?: string; to?: string }

export const RANGE_PRESETS: { id: RangePreset; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'all', label: 'All' },
]

export function isoDay(d: Date): string {
  const month = `${d.getMonth() + 1}`.padStart(2, '0')
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

/** Parse an ISO day as a LOCAL midnight, not the UTC instant `new Date(iso)` gives. */
export function parseIsoDay(iso: string): Date {
  return new Date(`${iso}T00:00:00`)
}

export function addDays(iso: string, days: number): string {
  const d = parseIsoDay(iso)
  d.setDate(d.getDate() + days)
  return isoDay(d)
}

/** Monday-based week start — the working week these worklists are read against. */
function startOfWeek(d: Date): Date {
  const out = new Date(d)
  // getDay(): 0 = Sunday. Sunday belongs to the week that began the Monday before.
  const shift = (out.getDay() + 6) % 7
  out.setDate(out.getDate() - shift)
  return out
}

/** The inclusive range a preset covers around `anchor`. */
export function presetRange(preset: RangePreset, anchor: Date): DisplayRange {
  if (preset === 'all') return null

  if (preset === 'today') {
    const day = isoDay(anchor)
    return { from: day, to: day }
  }

  if (preset === 'week') {
    const start = startOfWeek(anchor)
    const end = new Date(start)
    end.setDate(end.getDate() + 6)
    return { from: isoDay(start), to: isoDay(end) }
  }

  const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
  // Day 0 of the next month is the last day of this one — no month-length table.
  const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0)
  return { from: isoDay(start), to: isoDay(end) }
}

/** Step the anchor one preset-unit forward (+1) or back (-1). */
export function shiftAnchor(preset: RangePreset, anchor: Date, direction: 1 | -1): Date {
  const next = new Date(anchor)
  if (preset === 'today') next.setDate(next.getDate() + direction)
  else if (preset === 'week') next.setDate(next.getDate() + 7 * direction)
  else if (preset === 'month') {
    // Anchor to the 1st before stepping: from the 31st, +1 month lands in the
    // month after next, which would skip a month in the picker.
    next.setDate(1)
    next.setMonth(next.getMonth() + direction)
  }
  return next
}

/**
 * Convert the inclusive range the user sees into the half-open range both
 * endpoints expect. An inverted range yields no filter rather than a window
 * that silently matches nothing.
 */
export function toQueryRange(range: DisplayRange): QueryRange {
  if (!range) return {}
  if (range.to < range.from) return {}
  return { from: range.from, to: addDays(range.to, 1) }
}

/**
 * Is `day` inside the range currently on screen? Both ends inclusive, matching
 * `DisplayRange`. A null or inverted range is no date filter at all — the same
 * reading `toQueryRange` takes — so nothing can fall outside it.
 *
 * Exists to tell someone their just-created task is real but out of view. The
 * worklist opens on the current month, so a task created today and due next
 * year lands nowhere on screen, and silence there reads as a failed save.
 */
export function isWithinDisplayRange(day: string, range: DisplayRange): boolean {
  if (!range || range.to < range.from) return true
  // Tolerates a full ISO timestamp as well as a plain day: `dueDate` arrives
  // from the API as either, depending on the caller.
  const iso = day.slice(0, 10)
  return iso >= range.from && iso <= range.to
}

/**
 * Signed whole days from `fromIso` to `toIso` — negative when `to` is earlier.
 *
 * Used to translate a drag on the schedule board into a shift of a repeating
 * task's start date: dragging one card two days forward moves the whole series
 * two days forward, because there is one task behind every card.
 */
export function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((parseIsoDay(toIso).getTime() - parseIsoDay(fromIso).getTime()) / 86_400_000)
}

/** Inclusive length of a range in days. A single day is 1, not 0. */
export function rangeLengthDays(range: { from: string; to: string }): number {
  const ms = parseIsoDay(range.to).getTime() - parseIsoDay(range.from).getTime()
  return Math.max(1, Math.round(ms / 86_400_000) + 1)
}

/**
 * Step a hand-picked range by its own length, so the arrows keep working after
 * someone types explicit dates instead of picking a preset.
 */
export function shiftDisplayRange(
  range: { from: string; to: string },
  direction: 1 | -1,
): { from: string; to: string } {
  const step = rangeLengthDays(range) * direction
  return { from: addDays(range.from, step), to: addDays(range.to, step) }
}

/**
 * The reservations endpoint requires a window — it has no "everything" mode —
 * so `All` still has to name one.
 *
 * Kept under the server's 400-day ceiling (`parseWindowOr400`), which exists
 * because this feed carries patient MRNs and must not be pullable in one
 * unbounded sweep. Weighted backwards so holds still running from earlier stay
 * visible, and derived from the anchor so it does not drift between renders.
 */
export const ALL_WINDOW_LOOKBACK_DAYS = 120
export const ALL_WINDOW_LOOKAHEAD_DAYS = 270

export function allTimeWindow(anchor: Date): { from: string; to: string } {
  const day = isoDay(anchor)
  return {
    from: addDays(day, -ALL_WINDOW_LOOKBACK_DAYS),
    to: addDays(day, ALL_WINDOW_LOOKAHEAD_DAYS),
  }
}

/** Human label for the current range, e.g. "Aug 2026", "3 – 9 Aug 2026", "All dates". */
export function rangeLabel(preset: RangePreset, range: DisplayRange): string {
  if (!range) return 'All dates'

  const from = parseIsoDay(range.from)
  const to = parseIsoDay(range.to)

  if (preset === 'month') {
    return from.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
  }
  if (range.from === range.to) {
    return from.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  }
  const sameMonth = from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()
  const left = from.toLocaleDateString(
    undefined,
    sameMonth ? { day: 'numeric' } : { day: 'numeric', month: 'short' },
  )
  const right = to.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  return `${left} – ${right}`
}
